import { useEffect, useState } from 'react';
import { Button, Field, Input, Spinner, Text, Textarea, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { usePermission } from '../hooks/usePermission';
import { apiErrorMessage } from '../api/apiError';
import {
  fetchTraceabilityPlan,
  saveTraceabilityPlan,
  exportTraceabilityPlan,
  type TraceabilityPlan,
  type TraceabilityPlanInput,
} from '../api/traceability.api';
import { PageTitle } from '../components/shared/PageTitle';

const PLAN_KEY = ['traceability-plan'] as const;

const useStyles = makeStyles({
  page:    { display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px', maxWidth: '760px' },
  form:    { display: 'flex', flexDirection: 'column', gap: '12px' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' },
  muted:   { color: tokens.colorNeutralForeground3 },
});

const EMPTY: TraceabilityPlanInput = {
  recordsLocation: '',
  recordsFormat: '',
  foodIdentificationProcedure: '',
  tlcAssignmentProcedure: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  coveredFoodsNote: '',
};

function toForm(plan: TraceabilityPlan | null): TraceabilityPlanInput {
  if (!plan) return EMPTY;
  return {
    recordsLocation: plan.recordsLocation,
    recordsFormat: plan.recordsFormat,
    foodIdentificationProcedure: plan.foodIdentificationProcedure ?? '',
    tlcAssignmentProcedure: plan.tlcAssignmentProcedure,
    contactName: plan.contactName,
    contactPhone: plan.contactPhone,
    contactEmail: plan.contactEmail ?? '',
    coveredFoodsNote: plan.coveredFoodsNote ?? '',
  };
}

function downloadBase64(fileName: string, fileBase64: string) {
  const bytes = Uint8Array.from(atob(fileBase64), (ch) => ch.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes]));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TraceabilityPlanPage() {
  const t = useT();
  const c = t.traceabilityPlan;
  const s = useStyles();
  const qc = useQueryClient();
  const canWrite = usePermission('traceability:write');

  const { data: plan, isLoading, error } = useQuery({
    queryKey: PLAN_KEY,
    queryFn: fetchTraceabilityPlan,
  });

  const [form, setForm] = useState<TraceabilityPlanInput>(EMPTY);
  useEffect(() => { setForm(toForm(plan ?? null)); }, [plan]);

  const save = useMutation({
    mutationFn: () => saveTraceabilityPlan(form),
    onSuccess: (saved) => { qc.setQueryData(PLAN_KEY, saved); },
  });

  const exportFile = useMutation({
    mutationFn: (format: 'xlsx' | 'geojson') => exportTraceabilityPlan(format),
    onSuccess: ({ fileName, fileBase64 }) => downloadBase64(fileName, fileBase64),
  });

  const set = (key: keyof TraceabilityPlanInput) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  if (isLoading) return <Spinner label={c.title} />;

  return (
    <div className={s.page}>
      <PageTitle>{c.title}</PageTitle>
      <Text className={s.muted}>{c.subtitle}</Text>
      {error && <Text role="alert">{apiErrorMessage(error, c.saveError)}</Text>}
      {!plan && <Text className={s.muted}>{c.noPlan}</Text>}
      {plan && (
        <Text className={s.muted}>
          {c.version}: <span data-testid="plan-version">{plan.version}</span>
        </Text>
      )}
      {!canWrite && <Text className={s.muted}>{c.readOnly}</Text>}

      <div className={s.form}>
        <Field label={c.recordsLocation} required>
          <Input data-testid="plan-recordsLocation" value={form.recordsLocation} disabled={!canWrite} onChange={(_, d) => set('recordsLocation')(d.value)} />
        </Field>
        <Field label={c.recordsFormat} required>
          <Input data-testid="plan-recordsFormat" value={form.recordsFormat} disabled={!canWrite} onChange={(_, d) => set('recordsFormat')(d.value)} />
        </Field>
        <Field label={c.foodIdentification}>
          <Textarea data-testid="plan-foodIdentificationProcedure" value={form.foodIdentificationProcedure ?? ''} disabled={!canWrite} onChange={(_, d) => set('foodIdentificationProcedure')(d.value)} />
        </Field>
        <Field label={c.tlcProcedure} required>
          <Textarea data-testid="plan-tlcAssignmentProcedure" value={form.tlcAssignmentProcedure} disabled={!canWrite} onChange={(_, d) => set('tlcAssignmentProcedure')(d.value)} />
        </Field>
        <Field label={c.contactName} required>
          <Input data-testid="plan-contactName" value={form.contactName} disabled={!canWrite} onChange={(_, d) => set('contactName')(d.value)} />
        </Field>
        <Field label={c.contactPhone} required>
          <Input data-testid="plan-contactPhone" value={form.contactPhone} disabled={!canWrite} onChange={(_, d) => set('contactPhone')(d.value)} />
        </Field>
        <Field label={c.contactEmail}>
          <Input data-testid="plan-contactEmail" type="email" value={form.contactEmail ?? ''} disabled={!canWrite} onChange={(_, d) => set('contactEmail')(d.value)} />
        </Field>
      </div>

      {save.error && <Text role="alert">{apiErrorMessage(save.error, c.saveError)}</Text>}
      {save.isSuccess && <Text className={s.muted}>{c.saved}</Text>}

      <div className={s.actions}>
        {canWrite && (
          <Button appearance="primary" data-testid="plan-save" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? c.saving : c.save}
          </Button>
        )}
        <Button data-testid="plan-export-xlsx" disabled={exportFile.isPending} onClick={() => exportFile.mutate('xlsx')}>
          {c.exportXlsx}
        </Button>
        <Button data-testid="plan-export-geojson" disabled={exportFile.isPending} onClick={() => exportFile.mutate('geojson')}>
          {c.exportGeoJson}
        </Button>
      </div>
      {exportFile.error && <Text role="alert">{c.exportError}</Text>}
    </div>
  );
}
